import { forwardRef } from "react";
import type {
  ButtonHTMLAttributes,
  DetailedHTMLProps,
  AnchorHTMLAttributes,
} from "react";

// One button component to rule them all. Every ad-hoc `<button>` in the
// app should migrate to this over time so we can guarantee a single
// visual language. Height, padding, font, radius are driven by
// variant/size so we never have a nine-pixel offset sneaking in.
//
// Variants:
//   primary   — main actions. Solid accent.
//   secondary — supporting actions. Outlined.
//   ghost     — subtle actions. No border; hover-only background.
//   danger    — destructive. Muted-red outline.
//
// Sizes:
//   md (default) — 36px tall, used everywhere by default
//   sm           — 28px tall, for inside table rows + chips

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "md" | "sm";

interface CommonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  href?: string;
  leading?: React.ReactNode;
  trailing?: React.ReactNode;
}

type NativeButtonProps = Omit<
  DetailedHTMLProps<ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement>,
  "className"
>;
type NativeAnchorProps = Omit<
  DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement>,
  "className" | "href"
>;

type ButtonProps = CommonProps & NativeButtonProps & NativeAnchorProps & {
  className?: string;
};

export const Button = forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(function Button(
  {
    variant = "secondary",
    size = "md",
    href,
    leading,
    trailing,
    className,
    children,
    ...rest
  },
  ref,
) {
  const cls = [
    "ui-btn",
    `ui-btn-${variant}`,
    `ui-btn-${size}`,
    className,
  ]
    .filter(Boolean)
    .join(" ");
  if (href) {
    return (
      <a
        ref={ref as React.Ref<HTMLAnchorElement>}
        className={cls}
        href={href}
        {...(rest as unknown as NativeAnchorProps)}
      >
        {leading}
        {children}
        {trailing}
      </a>
    );
  }
  return (
    <button
      ref={ref as React.Ref<HTMLButtonElement>}
      type="button"
      className={cls}
      {...(rest as NativeButtonProps)}
    >
      {leading}
      {children}
      {trailing}
    </button>
  );
});
